/**
 * Shared authentication service for product applications (Athena, Toucan)
 *
 * Handles SSO authentication via ytest.ai identity service.
 * Login/signup happens via the identity service, this service manages
 * the tokens and session for product API access.
 *
 * Includes proactive token refresh — silently refreshes the access token
 * before it expires so active users never hit a 401.
 */
import { tokenStorage } from './storage';
/**
 * Decode the payload of a JWT without verifying the signature.
 * Safe for reading the `exp` claim on the client side.
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    }
    catch {
        return null;
    }
}
/**
 * Returns the number of milliseconds until the token expires.
 * Returns 0 if the token is already expired or unparseable.
 */
function msUntilExpiry(token) {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number')
        return 0;
    const expiresAtMs = payload.exp * 1000;
    return Math.max(0, expiresAtMs - Date.now());
}
// How far in advance (ms) to refresh before actual expiry
const REFRESH_BUFFER_MS = 2 * 60 * 1000; // 2 minutes
export function createAuthService(config) {
    const YTEST_URL = config?.ytestUrl || 'https://ytest.ai';
    const IDENTITY_API_URL = config?.identityApiUrl || 'https://api.ytest.ai';
    const TENANT_SLUG_KEY = 'tenant_slug';
    // Proactive refresh timer handle
    let refreshTimer = null;
    /**
     * Call the /auth/refresh endpoint with the stored refresh token.
     * On success, stores new tokens and reschedules the timer.
     * Returns true on success.
     */
    async function silentRefresh() {
        // No stored refresh token (signed in through the SSO cookie): send none,
        // and identity-api uses the httpOnly refresh_token cookie instead.
        const refreshToken = tokenStorage.getRefreshToken();
        try {
            const response = await fetch(`${IDENTITY_API_URL}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
                credentials: 'include',
            });
            if (!response.ok)
                return false;
            const data = await response.json();
            // Store new tokens
            service.setAccessToken(data.access_token);
            tokenStorage.setRefreshToken(data.refresh_token);
            // Reschedule next refresh based on new token's exp
            scheduleRefresh(data.access_token);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Schedule a proactive refresh 2 minutes before the access token expires.
     * If the token has less than 2 minutes left, refresh immediately.
     */
    function scheduleRefresh(accessToken) {
        // Clear any existing timer
        if (refreshTimer !== null) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }
        const remaining = msUntilExpiry(accessToken);
        if (remaining === 0)
            return; // token already expired or unparseable
        const delay = Math.max(0, remaining - REFRESH_BUFFER_MS);
        refreshTimer = setTimeout(() => {
            silentRefresh();
        }, delay);
    }
    /**
     * Stop the proactive refresh timer (e.g. on logout).
     */
    function stopRefreshTimer() {
        if (refreshTimer !== null) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
        }
    }
    const service = {
        /**
         * Initialize auth from URL token (SSO redirect from identity service)
         */
        initFromUrlToken() {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');
            const refresh = params.get('refresh_token');
            if (token) {
                this.setAccessToken(token);
                if (refresh) {
                    tokenStorage.setRefreshToken(refresh);
                }
                // Clean URL
                const url = new URL(window.location.href);
                url.searchParams.delete('token');
                url.searchParams.delete('refresh_token');
                window.history.replaceState({}, '', url.toString());
                // Start proactive refresh timer
                scheduleRefresh(token);
                return token;
            }
            return null;
        },
        /**
         * Redirect to identity service for login
         */
        redirectToLogin() {
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `${YTEST_URL}/login?return_to=${returnUrl}`;
        },
        /**
         * Redirect to identity service for signup
         */
        redirectToSignup() {
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `${YTEST_URL}/signup?return_to=${returnUrl}`;
        },
        logout() {
            stopRefreshTimer();
            tokenStorage.clearAll();
            localStorage.removeItem(TENANT_SLUG_KEY);
        },
        setAccessToken(token) {
            localStorage.setItem('access_token', token);
            localStorage.setItem('token', token);
        },
        getAccessToken() {
            return localStorage.getItem('access_token') || localStorage.getItem('token');
        },
        getRefreshToken() {
            return tokenStorage.getRefreshToken();
        },
        setUser(user) {
            tokenStorage.setUser(user);
        },
        getUser() {
            return tokenStorage.getUser();
        },
        setTenantSlug(slug) {
            localStorage.setItem(TENANT_SLUG_KEY, slug);
        },
        getTenantSlug() {
            return localStorage.getItem(TENANT_SLUG_KEY);
        },
        isAuthenticated() {
            return !!this.getUser();
        },
        /**
         * Validate token with identity service and get user data.
         * Also starts the proactive refresh timer on success.
         */
        async validateToken() {
            try {
                const token = this.getAccessToken();
                const headers = {};
                if (token && token !== 'null') {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                const response = await fetch(`${IDENTITY_API_URL}/api/v1/auth/me`, {
                    credentials: 'include',
                    headers,
                });
                if (!response.ok) {
                    // Token invalid — try a silent refresh before giving up
                    const refreshed = await silentRefresh();
                    if (refreshed) {
                        // Retry validation with new token
                        const newToken = this.getAccessToken();
                        if (newToken) {
                            const retryResponse = await fetch(`${IDENTITY_API_URL}/api/v1/auth/me`, {
                                credentials: 'include',
                                headers: { Authorization: `Bearer ${newToken}` },
                            });
                            if (retryResponse.ok) {
                                const data = await retryResponse.json();
                                this.setUser(data.user);
                                this.setTenantSlug(data.tenant.slug);
                                return data;
                            }
                        }
                    }
                    this.clearAuth();
                    return null;
                }
                const data = await response.json();
                if (data.tokens?.access_token) {
                    this.setAccessToken(data.tokens.access_token);
                    if (data.tokens.refresh_token) {
                        tokenStorage.setRefreshToken(data.tokens.refresh_token);
                    }
                }
                this.setUser(data.user);
                this.setTenantSlug(data.tenant.slug);
                // Start proactive refresh timer based on current access token
                const currentToken = this.getAccessToken();
                if (currentToken) {
                    scheduleRefresh(currentToken);
                }
                return data;
            }
            catch {
                return null;
            }
        },
        clearAuth() {
            stopRefreshTimer();
            localStorage.removeItem('access_token');
            localStorage.removeItem('token');
            localStorage.removeItem(TENANT_SLUG_KEY);
            tokenStorage.clearAll();
        },
        /**
         * Manually trigger a silent token refresh (used by ApiClient 401 interceptor).
         */
        silentRefresh,
    };
    // On service creation, if we already have a token in storage, schedule refresh
    const existingToken = service.getAccessToken();
    if (existingToken) {
        scheduleRefresh(existingToken);
    }
    return service;
}
// Export a default instance for backwards compatibility
export const authService = createAuthService();
//# sourceMappingURL=service.js.map